import {
  invalidateWorkspaceRead,
  readWorkspaceCached,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import {
  addWorkspaceEmbeddingIgnoredDocsMutation,
  getAllWorkspaceEmbeddingIgnoredDocsQuery,
  getWorkspaceConfigQuery,
  getWorkspaceEmbeddingStatusQuery,
  reindexWorkspaceEmbeddingsMutation,
  removeWorkspaceEmbeddingIgnoredDocsMutation,
  setEnableDocEmbeddingMutation,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

export class EmbeddingStore extends Store {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  async getEnabled(workspaceId: string, signal?: AbortSignal) {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No Server');
    }
    const data = await readWorkspaceCached({
      workspaceId,
      key: 'workspace-config',
      signal,
      load: () =>
        server.gql({
          query: getWorkspaceConfigQuery,
          variables: { id: workspaceId },
        }),
    });
    return data.workspace.enableDocEmbedding;
  }

  async updateEnabled(
    workspaceId: string,
    enabled: boolean,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    await this.workspaceServerService.server.gql({
      query: setEnableDocEmbeddingMutation,
      variables: {
        id: workspaceId,
        enableDocEmbedding: enabled,
      },
      context: {
        signal,
      },
    });
    invalidateWorkspaceRead(workspaceId, 'workspace-config');
  }

  async getIgnoredDocs(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }

    const data = await this.workspaceServerService.server.gql({
      query: getAllWorkspaceEmbeddingIgnoredDocsQuery,
      variables: {
        workspaceId,
      },
      context: { signal },
    });
    return data.workspace.embedding.allIgnoredDocs;
  }

  async updateIgnoredDocs(
    workspaceId: string,
    add: string[],
    remove: string[],
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }

    await Promise.all([
      this.workspaceServerService.server.gql({
        query: addWorkspaceEmbeddingIgnoredDocsMutation,
        variables: {
          workspaceId,
          add,
        },
        context: { signal },
      }),
      this.workspaceServerService.server.gql({
        query: removeWorkspaceEmbeddingIgnoredDocsMutation,
        variables: {
          workspaceId,
          remove,
        },
        context: { signal },
      }),
    ]);
  }

  async getEmbeddingProgress(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }

    const data = await this.workspaceServerService.server.gql({
      query: getWorkspaceEmbeddingStatusQuery,
      variables: {
        workspaceId,
      },
      context: { signal },
    });
    return data.queryWorkspaceEmbeddingStatus;
  }

  /**
   * Re-index embeddings (delete + queue): all docs of the workspace, or only
   * `docIds` when given.
   */
  async reindexWorkspace(
    workspaceId: string,
    docIds?: string[],
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }

    const data = await this.workspaceServerService.server.gql({
      query: reindexWorkspaceEmbeddingsMutation,
      variables: { workspaceId, docIds },
      context: { signal },
    });
    return data.reindexWorkspaceEmbeddings;
  }
}
